import React, { useMemo, useState } from "react";
import { motion } from "framer-motion";
import { Sun, Battery, Zap, TreePine, DollarSign, RotateCcw, MapPin, Search, Image as ImageIcon, Loader2 } from "lucide-react";
import { Link } from "react-router-dom";

/**
 * Interactive 2D Solar Configurator (Aurora-lite).
 *
 * v2 adds an optional satellite background (Esri World Imagery — free,
 * no API key) centred on either SXM or a typed address. The user
 * paints panels onto the 8×5 grid that overlays the roof imagery.
 *
 * Math is unchanged from v1:
 *   panels × 400 W → kW → kWh/day via PEAK_SUN_HOURS × roof-tilt factor
 *   → annual savings @ $0.36/kWh → 25-yr lifetime with light degradation.
 */
const PANEL_W = 400;
const PEAK_SUN_HOURS = 5.5;
const RATE_USD_PER_KWH = 0.36;
const ROOF_TILT_FACTOR = { flat: 0.85, asphalt: 1.0, metal: 1.05, concrete: 0.95 };

const GRID_COLS = 8;
const GRID_ROWS = 5;

// SXM centre (Philipsburg) — fallback when no address geocoded.
const DEFAULT_CENTRE = { lat: 18.0425, lng: -63.0548, zoom: 19 };

// Esri WorldImagery tile — high-res satellite, free, no key.
const tileUrl = (z, x, y) => `https://server.arcgisonline.com/ArcGIS/rest/services/World_Imagery/MapServer/tile/${z}/${y}/${x}`;

function deg2num(lat, lng, zoom) {
  const latRad = (lat * Math.PI) / 180;
  const n = 2 ** zoom;
  const x = ((lng + 180) / 360) * n;
  const y = ((1 - Math.log(Math.tan(latRad) + 1 / Math.cos(latRad)) / Math.PI) / 2) * n;
  return { x, y };
}

// 3×3 tile grid centred on (lat,lng). Returns 9 tiles with relative offsets.
function tileGrid(lat, lng, zoom) {
  const { x, y } = deg2num(lat, lng, zoom);
  const cx = Math.floor(x); const cy = Math.floor(y);
  const tiles = [];
  for (let dy = -1; dy <= 1; dy++) {
    for (let dx = -1; dx <= 1; dx++) {
      tiles.push({ z: zoom, x: cx + dx, y: cy + dy, dx, dy });
    }
  }
  // Sub-pixel offset so the centre of the user's lat/lng sits at the centre.
  const offsetX = (x - cx) * 256;
  const offsetY = (y - cy) * 256;
  return { tiles, offsetX, offsetY };
}

export default function SolarConfigurator({ embedded = false }) {
  const [roof, setRoof] = useState("asphalt");
  const [active, setActive] = useState(() => {
    const s = new Set();
    for (let r = 1; r < 3; r++) for (let c = 1; c < 6; c++) s.add(`${r}-${c}`);
    return s;
  });
  const [battery, setBattery] = useState(0);

  // ----- Satellite background state -----
  const [bgMode, setBgMode] = useState("dark"); // dark | satellite
  const [centre, setCentre] = useState(DEFAULT_CENTRE);
  const [address, setAddress] = useState("");
  const [geocoding, setGeocoding] = useState(false);
  const [geoError, setGeoError] = useState("");

  const toggle = (key) => setActive((prev) => {
    const next = new Set(prev);
    if (next.has(key)) next.delete(key); else next.add(key);
    return next;
  });

  const count = active.size;

  const calc = useMemo(() => {
    const tilt = ROOF_TILT_FACTOR[roof] || 1.0;
    const systemKw = (count * PANEL_W) / 1000;
    const dailyKwh = systemKw * PEAK_SUN_HOURS * tilt;
    const monthlyKwh = dailyKwh * 30.4;
    const annualKwh = dailyKwh * 365;
    const annualSavings = annualKwh * RATE_USD_PER_KWH;
    const lifetime = annualSavings * 25 * 0.94;
    const co2Tons = annualKwh * 0.0005;
    const systemPrice = count * PANEL_W * 2.5 + battery * 400;
    const paybackYears = annualSavings > 0 ? systemPrice / annualSavings : 0;
    return { systemKw, monthlyKwh, annualKwh, annualSavings, lifetime, co2Tons, systemPrice, paybackYears };
  }, [count, roof, battery]);

  const fmt$ = (n) => n.toLocaleString(undefined, { maximumFractionDigits: 0 });
  const fmtK = (n) => n.toLocaleString(undefined, { maximumFractionDigits: 1 });

  // Free reverse geocode via OSM Nominatim. SXM-biased viewbox.
  const geocode = async () => {
    if (!address.trim()) return;
    setGeocoding(true);
    setGeoError("");
    try {
      const params = new URLSearchParams({
        q: `${address}, Sint Maarten`,
        format: "json",
        limit: "1",
        countrycodes: "sx,mf,an",
      });
      const r = await fetch(`https://nominatim.openstreetmap.org/search?${params.toString()}`, {
        headers: { Accept: "application/json" },
      });
      const j = await r.json();
      if (!Array.isArray(j) || j.length === 0) {
        setGeoError("Couldn't find that address on SXM");
        return;
      }
      setCentre({ lat: parseFloat(j[0].lat), lng: parseFloat(j[0].lon), zoom: 19 });
      setBgMode("satellite");
    } catch (err) {
      setGeoError("Geocoding failed — try again");
    } finally {
      setGeocoding(false);
    }
  };

  const grid = useMemo(() => tileGrid(centre.lat, centre.lng, centre.zoom), [centre]);

  return (
    <div data-testid="solar-configurator" className={embedded ? "" : "min-h-screen bg-[#F8FAFC] dark:bg-slate-950 py-12 px-6"}>
      <div className="max-w-6xl mx-auto">
        {!embedded && (
          <div className="mb-8 text-center">
            <p className="overline">Design your system</p>
            <h1 className="font-heading text-4xl sm:text-5xl text-[#0A2540] dark:text-white mt-2">
              Build your <span className="text-[#C9A356]">solar setup</span>
            </h1>
            <p className="text-[#475569] dark:text-slate-400 mt-3 max-w-xl mx-auto">
              Drop your address to overlay panels on your actual roof. We'll size the system, model production, and project savings live.
            </p>
          </div>
        )}

        <div className="grid lg:grid-cols-[1fr_320px] gap-6">
          <div className="card-pb p-6">
            {/* Top controls row */}
            <div className="flex flex-wrap gap-3 items-end justify-between mb-4">
              <div>
                <div className="text-xs uppercase tracking-wider font-bold text-[#475569] dark:text-slate-400 mb-1">Roof type</div>
                <div className="inline-flex bg-slate-100 dark:bg-slate-800 rounded-lg p-1">
                  {[
                    { id: "asphalt", label: "Asphalt" },
                    { id: "metal", label: "Metal" },
                    { id: "concrete", label: "Concrete" },
                    { id: "flat", label: "Flat" },
                  ].map((r) => (
                    <button key={r.id} onClick={() => setRoof(r.id)}
                            data-testid={`roof-${r.id}`}
                            className={`text-xs px-3 py-1.5 rounded-md transition ${
                              roof === r.id ? "bg-white dark:bg-slate-900 text-[#0A2540] dark:text-white shadow-sm font-medium" : "text-[#475569] dark:text-slate-400 hover:text-[#0A2540] dark:hover:text-white"
                            }`}>
                      {r.label}
                    </button>
                  ))}
                </div>
              </div>

              <div className="flex items-center gap-2">
                <div className="inline-flex bg-slate-100 dark:bg-slate-800 rounded-lg p-1">
                  <button onClick={() => setBgMode("dark")} data-testid="bg-dark"
                          className={`text-xs px-3 py-1.5 rounded-md transition inline-flex items-center gap-1 ${
                            bgMode === "dark" ? "bg-white dark:bg-slate-900 text-[#0A2540] dark:text-white shadow-sm font-medium" : "text-[#475569] dark:text-slate-400"
                          }`}>
                    <ImageIcon className="w-3 h-3" /> Studio
                  </button>
                  <button onClick={() => setBgMode("satellite")} data-testid="bg-satellite"
                          className={`text-xs px-3 py-1.5 rounded-md transition inline-flex items-center gap-1 ${
                            bgMode === "satellite" ? "bg-white dark:bg-slate-900 text-[#0A2540] dark:text-white shadow-sm font-medium" : "text-[#475569] dark:text-slate-400"
                          }`}>
                    <MapPin className="w-3 h-3" /> Satellite
                  </button>
                </div>
                <button onClick={() => setActive(new Set())} className="text-xs text-[#475569] hover:text-[#0A2540] inline-flex items-center gap-1" data-testid="clear-roof">
                  <RotateCcw className="w-3 h-3" /> Clear
                </button>
              </div>
            </div>

            {bgMode === "satellite" && (
              <div className="flex gap-2 mb-3">
                <div className="flex-1 relative">
                  <Search className="w-3.5 h-3.5 text-[#94A3B8] absolute left-3 top-1/2 -translate-y-1/2" />
                  <input
                    type="text"
                    value={address}
                    onChange={(e) => setAddress(e.target.value)}
                    onKeyDown={(e) => { if (e.key === "Enter") geocode(); }}
                    placeholder="Your address on SXM (e.g. 12 Welfare Rd, Cole Bay)"
                    data-testid="configurator-address"
                    className="w-full pl-9 pr-3 py-2 text-sm rounded-md border border-[#E2E8F0] dark:border-slate-700 bg-white dark:bg-slate-900 outline-none focus:ring-2 focus:ring-[#C9A356]/40"
                  />
                </div>
                <button
                  onClick={geocode}
                  disabled={geocoding || !address.trim()}
                  data-testid="configurator-locate"
                  className="btn-accent text-sm disabled:opacity-50"
                >
                  {geocoding ? <Loader2 className="w-4 h-4 animate-spin" /> : "Locate"}
                </button>
              </div>
            )}
            {geoError && <div className="text-xs text-rose-500 mb-3" data-testid="configurator-geo-error">{geoError}</div>}

            {/* Canvas */}
            <div className={`relative rounded-xl overflow-hidden shadow-inner ${
              bgMode === "dark" ? "bg-gradient-to-b from-slate-700 to-slate-900" : "bg-slate-900"
            }`} style={{ height: 360 }}>
              {bgMode === "satellite" && (
                <div className="absolute inset-0 overflow-hidden" data-testid="satellite-bg">
                  <div className="absolute" style={{
                    left: `calc(50% - 384px - ${grid.offsetX}px)`,
                    top:  `calc(50% - 384px - ${grid.offsetY}px)`,
                    width: 768, height: 768,
                  }}>
                    {grid.tiles.map((t) => (
                      <img
                        key={`${t.x}-${t.y}`}
                        src={tileUrl(t.z, t.x, t.y)}
                        alt=""
                        loading="lazy"
                        crossOrigin="anonymous"
                        className="absolute"
                        style={{ left: (t.dx + 1) * 256, top: (t.dy + 1) * 256, width: 256, height: 256 }}
                        onError={(e) => { e.currentTarget.style.opacity = 0.3; }}
                      />
                    ))}
                  </div>
                  {/* Dim overlay so panels stand out */}
                  <div className="absolute inset-0 bg-black/30" />
                </div>
              )}

              <div className="absolute top-2 right-3 text-[10px] uppercase tracking-wider text-white/60">
                {bgMode === "satellite" ? "Live satellite · Esri" : `Top-down · ${roof}`}
              </div>

              {/* Panel grid overlay */}
              <div className="absolute inset-0 grid place-items-center p-6">
                <div className="grid gap-1.5 w-full max-w-[560px]" style={{ gridTemplateColumns: `repeat(${GRID_COLS}, 1fr)` }}>
                  {Array.from({ length: GRID_ROWS }).map((_, r) =>
                    Array.from({ length: GRID_COLS }).map((__, c) => {
                      const key = `${r}-${c}`;
                      const on = active.has(key);
                      return (
                        <motion.button
                          key={key}
                          onClick={() => toggle(key)}
                          data-testid={`cell-${key}`}
                          whileHover={{ scale: 1.05 }}
                          whileTap={{ scale: 0.94 }}
                          className={`aspect-square rounded-md transition-colors ${
                            on
                              ? "bg-gradient-to-br from-[#C9A356] to-[#9c7a32] shadow-[inset_0_-1px_0_rgba(0,0,0,0.25),0_2px_4px_rgba(201,163,86,0.4)]"
                              : bgMode === "satellite"
                                ? "bg-white/10 hover:bg-white/25 border border-white/30 backdrop-blur-sm"
                                : "bg-white/5 hover:bg-white/15 border border-white/10"
                          }`}
                          aria-label={`Toggle panel at row ${r} col ${c}`}
                        >
                          {on && <Sun className="w-3 h-3 sm:w-4 sm:h-4 m-auto text-[#0A2540]/60" />}
                        </motion.button>
                      );
                    }),
                  )}
                </div>
              </div>

              {count === 0 && (
                <div className="absolute inset-0 grid place-items-center pointer-events-none">
                  <div className="text-white/70 text-sm bg-black/30 backdrop-blur-sm px-3 py-1.5 rounded-md">
                    Click cells to place solar panels
                  </div>
                </div>
              )}
            </div>

            {/* Battery selector */}
            <div className="mt-6">
              <div className="flex items-center justify-between mb-2">
                <div className="flex items-center gap-2 text-sm text-[#0A2540] dark:text-white font-medium">
                  <Battery className="w-4 h-4 text-[#C9A356]" /> Battery storage
                </div>
                <span className="text-xs text-[#475569] dark:text-slate-400">{battery} kWh</span>
              </div>
              <div className="grid grid-cols-5 gap-2">
                {[0, 5, 10, 15, 20].map((b) => (
                  <button key={b} onClick={() => setBattery(b)}
                          data-testid={`battery-${b}`}
                          className={`py-2 text-xs font-medium rounded-md border-2 transition ${
                            battery === b
                              ? "border-[#C9A356] bg-[#FFFBF0] dark:bg-[#3a2e16] text-[#C9A356]"
                              : "border-[#E2E8F0] dark:border-slate-700 text-[#475569] dark:text-slate-300 hover:border-[#C9A356]"
                          }`}>
                    {b === 0 ? "None" : `${b} kWh`}
                  </button>
                ))}
              </div>
            </div>
          </div>

          {/* RIGHT: numbers + CTA */}
          <div className="space-y-4">
            <KpiTile icon={Zap} label="System size" value={`${fmtK(calc.systemKw)} kW`} sub={`${count} panels × 400 W`} accent />
            <KpiTile icon={DollarSign} label="Annual savings" value={`$${fmt$(calc.annualSavings)}`} sub={`vs. $${RATE_USD_PER_KWH.toFixed(2)}/kWh local rate`} />
            <KpiTile icon={Sun} label="Monthly production" value={`${fmt$(calc.monthlyKwh)} kWh`} sub={`${PEAK_SUN_HOURS}h peak sun · ${roof}`} />
            <KpiTile icon={TreePine} label="CO₂ offset" value={`${fmtK(calc.co2Tons)} t/yr`} sub={`~${Math.round(calc.co2Tons * 47)} trees equivalent`} />

            <div className="card-pb p-5 bg-[#0A2540] text-white">
              <div className="overline text-[#C9A356] mb-1">25-year savings</div>
              <div className="font-heading text-4xl font-bold tabular-nums">${fmt$(calc.lifetime)}</div>
              <div className="text-xs text-white/60 mt-1">Payback in <span className="text-[#C9A356] font-semibold">{calc.paybackYears > 0 ? `${fmtK(calc.paybackYears)} yrs` : "—"}</span> on a <span className="font-semibold">${fmt$(calc.systemPrice)}</span> system</div>
            </div>

            <Link to={`/services?roof=${roof}&panels=${count}&battery=${battery}`} className="btn-accent w-full" data-testid="configurator-cta">
              Get this exact system quoted →
            </Link>
            <div className="text-[10px] text-[#94A3B8] text-center px-2">
              Estimates only. Final system size depends on roof shading, orientation and inspection.
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

function KpiTile({ icon: Icon, label, value, sub, accent }) {
  return (
    <div className={`card-pb p-4 ${accent ? "border-2 border-[#C9A356]" : ""}`}>
      <div className="flex items-center gap-2">
        <Icon className={`w-4 h-4 ${accent ? "text-[#C9A356]" : "text-[#475569] dark:text-slate-400"}`} />
        <span className="overline">{label}</span>
      </div>
      <div className="font-heading text-2xl font-bold text-[#0A2540] dark:text-white mt-1 tabular-nums">{value}</div>
      <div className="text-[11px] text-[#94A3B8] mt-0.5">{sub}</div>
    </div>
  );
}
